import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { Switch } from "@/components/ui/switch";
import { UseFormReturn } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";

interface NoteFormFieldsProps {
  form: UseFormReturn<any>;
  isSubmitting: boolean;
  noteTypeOptions: any[];
  onNoteTypeChange: (type: string) => void;
  watchedNoteType: string;
  watchedIsInternal: boolean;
}

export const NoteFormFields: React.FC<NoteFormFieldsProps> = ({ 
  form, isSubmitting, noteTypeOptions, onNoteTypeChange, watchedNoteType, watchedIsInternal
}) => {
  return (
    <>
      <FormField
        control={form.control}
        name="note_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Note Type</FormLabel>
            <FormControl>
              <ResponsiveCommandSelect
                value={field.value}
                onValueChange={onNoteTypeChange}
                options={noteTypeOptions}
                disabled={isSubmitting}
                title="Note Type"
                description="Select the type of note to create"
                placeholder="Select note type"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Title (Optional)</FormLabel>
            <FormControl>
              <Input
                placeholder={
                  watchedNoteType === "proposal" ? "Proposal or quote reference" :
                  watchedNoteType === "meeting" ? "Meeting subject or topic" :
                  watchedNoteType === "call" ? "Call purpose or outcome" : "Note title or summary"
                }
                {...field}
                disabled={isSubmitting}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="content"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Note Content</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Add your note content here..."
                className="min-h-[150px] resize-y"
                {...field}
                disabled={isSubmitting}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {watchedNoteType !== "internal" && (
        <FormField
          control={form.control}
          name="is_internal"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base flex items-center gap-2">
                  {watchedIsInternal ? <EyeOff size={16} /> : <Eye size={16} />}
                  Internal Note
                </FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </>
  );
};
